import { Fragment, useEffect, useMemo, useState } from "react";

import { api } from "../api.js";
import { ChartCard, InfoPopover } from "../components/ChartCard.js";
import { BarChart } from "../components/charts/BarChart.js";
import { KpiRow } from "../components/KpiRow.js";
import { navigateOrOpenNewTab } from "../lib/dashboardNav.js";
import { formatEventTimeLocal } from "../lib/dashboardTime.js";
import { buildRawEventsUrl } from "../lib/rawEventsUrl.js";
import { useReportingTimezone } from "../lib/ReportingTimezoneContext.js";
import type {
  FilterDevice,
  FilterState,
  FilterVenue,
  InteractionSessionCohort,
  OpenRawEventsHandler,
  SessionsCohortProfile,
  SessionsData,
  SessionsLongSessionRow,
} from "../types.js";

const COHORT_ORDER: InteractionSessionCohort[] = ["<1m", "1-5m", "5-30m", "30m+"];

const PAID_FILL = "#4f80c7";
const FREE_FILL = "#3d5a80";

function fmtPct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function fmtMs(ms: number): string {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  const h = Math.floor(ms / 3_600_000);
  const m = Math.round((ms % 3_600_000) / 60_000);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function truncateId(id: string, n = 10): string {
  if (id.length <= n * 2 + 1) return id;
  return `${id.slice(0, n)}…${id.slice(-n)}`;
}

/** Last 4 characters of a session id (or the whole id if shorter). */
function sessionIdTail(id: string): string {
  return id.length <= 4 ? id : id.slice(-4);
}

function deviceDisplayName(
  row: SessionsLongSessionRow,
  deviceLabelById: Map<string, string>,
): string {
  const fromFilters = deviceLabelById.get(row.deviceId);
  if (fromFilters) return fromFilters;
  const name = row.deviceName.trim();
  if (name) return name;
  return truncateId(row.deviceId, 6);
}

function venueDisplayName(row: SessionsLongSessionRow, venueLabelById: Map<string, string>): string {
  const fromFilters = row.venueId ? venueLabelById.get(row.venueId) : undefined;
  if (fromFilters) return fromFilters;
  return row.venueName.trim();
}

function sessionExplorerFilterNote(
  longOnly: boolean,
  paidOnly: boolean,
  minSpanMs: number,
): string {
  if (!longOnly && !paidOnly) return "all interaction stints";
  if (longOnly && paidOnly) return `${fmtMs(minSpanMs)}+ wall or 💵 paid session`;
  if (longOnly) return `${fmtMs(minSpanMs)}+ wall only`;
  return "💵 paid session only";
}

function pageNumbersForPager(current: number, total: number): number[] {
  if (total <= 1) return [1];
  if (total <= 14) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const want = new Set<number>([1, total, current, current - 1, current + 1, current - 2, current + 2]);
  for (let p = 1; p <= 3; p++) want.add(p);
  for (let p = total - 2; p <= total; p++) want.add(p);
  return [...want].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
}

function ExplorerColumnHeader({ label, info }: { label: string; info: string }) {
  return (
    <th className="px-2 py-2">
      <span className="inline-flex items-center gap-0.5 normal-case tracking-normal">
        {label}
        <InfoPopover text={info} label={`${label} column`} />
      </span>
    </th>
  );
}

interface SessionsPanelProps {
  data: SessionsData | null;
  filters: FilterState;
  listOffset: number;
  listLimit: number;
  longOnly: boolean;
  paidOnly: boolean;
  onLongOnlyChange: (on: boolean) => void;
  onPaidOnlyChange: (on: boolean) => void;
  onListPageSizeChange: (n: number) => void;
  onListPrevPage: () => void;
  onListNextPage: () => void;
  onGoToPage: (page: number) => void;
  onOpenRawEvents: OpenRawEventsHandler;
}

function CohortProfileBlock({
  profile,
  onOpenRawEvents,
}: {
  profile: SessionsCohortProfile;
  onOpenRawEvents: OpenRawEventsHandler;
}) {
  const shellTotal = profile.shellMs.total || 1;
  const shellPct = {
    attract: profile.shellMs.attract / shellTotal,
    menu: profile.shellMs.mainMenu / shellTotal,
    game: profile.shellMs.game / shellTotal,
  };

  return (
    <ChartCard
      title={`Cohort: ${profile.cohort} (${profile.stintCount.toLocaleString()} stints)`}
      criteria={`interaction_session_id stints with wall span in bucket ${profile.cohort} (max−min client_timestamp in range).

Top events: event_name counts across all rows for those ids (same dashboard filters).
Outcomes: % of stints with bool flags (menu screen_exited, game_started, on the Retention paid session list including shell gap, game_completed, game_abandoned).
Shell ms: sum of screen_exited.duration_ms by screen_name (attract / main_menu / game).
Medians: percentile_cont(0.5) of wall_ms, active_ms, event_count per stint.

Event bar click: Raw Events → event_name + interaction_session_cohort=${profile.cohort}.`}
      note={`Median wall ${fmtMs(profile.medians.wallMs)} · active ${fmtMs(profile.medians.activeMs)} · ${Math.round(profile.medians.eventCount)} events`}
    >
      <div className="mb-3 grid grid-cols-2 gap-2 text-[11px] text-text-secondary sm:grid-cols-5">
        <span>Menu: {fmtPct(profile.outcomes.reachedMenuPct)}</span>
        <span>Game start: {fmtPct(profile.outcomes.hadGameStartPct)}</span>
        <span>Payment: {fmtPct(profile.outcomes.hadPaymentPct)}</span>
        <span>Completed: {fmtPct(profile.outcomes.hadGameCompletedPct)}</span>
        <span>Abandoned: {fmtPct(profile.outcomes.hadGameAbandonedPct)}</span>
      </div>
      <p className="mb-2 text-[11px] text-text-secondary">
        Shell dwell mix: attract {fmtPct(shellPct.attract)} · menu {fmtPct(shellPct.menu)} · game{" "}
        {fmtPct(shellPct.game)}
      </p>
      <BarChart
        data={profile.topEvents.map((e) => ({
          label: e.eventName,
          value: e.pct,
        }))}
        percent
        verbatimAxisLabels
        axisLabelMaxChars={14}
        barTooltip={(item) =>
          `${item.label}: ${fmtPct(item.value)} of cohort events — click: Raw Events → ${item.label} + cohort ${profile.cohort}. Middle-click / Ctrl/Cmd+click: new tab.`
        }
        onBarClick={(item, _i, _si, e) =>
          onOpenRawEvents(
            {
              eventName: item.label,
              interactionSessionCohort: profile.cohort,
            },
            e,
          )
        }
      />
    </ChartCard>
  );
}

export function SessionsPanel({
  data,
  filters,
  listOffset,
  listLimit,
  longOnly,
  paidOnly,
  onLongOnlyChange,
  onPaidOnlyChange,
  onListPageSizeChange,
  onListPrevPage,
  onListNextPage,
  onGoToPage,
  onOpenRawEvents,
}: SessionsPanelProps) {
  const [jumpDraft, setJumpDraft] = useState("1");
  const [devices, setDevices] = useState<FilterDevice[]>([]);
  const [venues, setVenues] = useState<FilterVenue[]>([]);

  const reportingTz = useReportingTimezone();

  useEffect(() => {
    api
      .filters()
      .then((incoming) => {
        setDevices(incoming.devices ?? []);
        setVenues(incoming.venues ?? []);
      })
      .catch(() => {});
  }, []);

  const deviceLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of devices) {
      m.set(d.id, (d.name && d.name.trim()) || d.id);
    }
    return m;
  }, [devices]);

  const venueLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of venues) {
      m.set(v.id, (v.name && v.name.trim()) || v.id);
    }
    return m;
  }, [venues]);

  if (!data) {
    return <p className="py-5 text-[13px] text-text-secondary">No data available</p>;
  }

  const { summary, orphanProfile, cohortProfiles, longSessions } = data;
  const cohortProfileByKey = new Map(cohortProfiles.map((p) => [p.cohort, p]));
  const filterNote = sessionExplorerFilterNote(
    longSessions.longOnly,
    longSessions.paidOnly,
    longSessions.minSpanMs,
  );
  const listAllStints = longSessions.listAllStints;
  const explorerCountMismatch = longSessions.total !== summary.stintCount;
  const showUnexpectedMismatchBanner = listAllStints && explorerCountMismatch;

  const durationBars = COHORT_ORDER.map((c) => ({
    label: c,
    value: summary.cohortCounts[c] ?? 0,
  }));

  const paidFreeBars = COHORT_ORDER.map((c) => {
    const pf = summary.paidVsFreeByCohort[c] ?? { paid: 0, free: 0 };
    const total = pf.paid + pf.free;
    return {
      label: c,
      value: total,
      stack: [
        { value: pf.paid, fill: PAID_FILL },
        { value: pf.free, fill: FREE_FILL },
      ],
    };
  });

  const listPage = Math.floor(listOffset / listLimit) + 1;
  const listTotalPages = Math.max(1, Math.ceil(longSessions.total / listLimit));
  const listRangeStart = longSessions.total === 0 ? 0 : listOffset + 1;
  const listRangeEnd = Math.min(listOffset + longSessions.rows.length, longSessions.total);
  const listHasMore = listOffset + listLimit < longSessions.total;
  const pageNums = pageNumbersForPager(listPage, listTotalPages);

  function submitJump() {
    const p = Number.parseInt(jumpDraft, 10);
    if (!Number.isFinite(p)) return;
    onGoToPage(p);
  }

  const emptyExplorerMessage =
    longSessions.longOnly && longSessions.paidOnly
      ? "No stints match Long or Paid filters in this range."
      : longSessions.longOnly
        ? `No stints with wall span ≥ ${fmtMs(longSessions.minSpanMs)} in this range.`
        : longSessions.paidOnly
          ? "No paid stints (payment_succeeded with interaction_session_id) in this range."
          : "No interaction stints in this range with current filters.";

  const explorerNote = listAllStints
    ? `Showing ${listRangeStart}–${listRangeEnd} of ${longSessions.total.toLocaleString()} interaction stints. Sorted by first event time (newest first).`
    : `Showing ${listRangeStart}–${listRangeEnd} of ${longSessions.total.toLocaleString()} matching stints (${summary.stintCount.toLocaleString()} interaction stints in scope) · ${filterNote}`;

  return (
    <>
      <KpiRow
        items={[
          {
            name: "Interaction stints",
            value: summary.stintCount.toLocaleString(),
            sub: "distinct non-empty interaction_session_id",
          },
          {
            name: "Orphan events",
            value: summary.orphanEventCount.toLocaleString(),
            sub: fmtPct(summary.orphanEventPct) + " of events in scope (no id)",
          },
          {
            name: "Session explorer",
            value: longSessions.total.toLocaleString(),
            sub: listAllStints
              ? "all interaction stints in scope"
              : filterNote,
          },
          {
            name: "Events in scope",
            value: summary.totalEvents.toLocaleString(),
            sub: "all device_events rows",
          },
        ]}
      />

      <ChartCard
        title="Session explorer"
        criteria={`Per interaction_session_id metrics subquery. Optional filters (checkboxes):
• **Long** — wall span ≥ min_span_ms (default 30m).
• **Paid 💵** — this stint’s interaction_session_id is on the Retention paid session list (registered or shell gap; same ISID resolution). A non-empty paid_session_id alone does not qualify.
With both checked, stints matching **either** filter are listed (OR). With neither checked, all stints in scope are listed.

Sorted by first client_timestamp desc (newest stint start first). Row click: Raw Events → exact interaction_session_id.`}
        note={explorerNote}
        titleRight={
          <div className="flex flex-wrap items-center gap-3 text-[11px] text-text-secondary">
            <label className="inline-flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                className="accent-accent-2"
                checked={longOnly}
                onChange={(e) => onLongOnlyChange(e.target.checked)}
              />
              <span className="inline-flex items-center gap-0.5">
                Long ({fmtMs(longSessions.minSpanMs)}+)
                <InfoPopover
                  text={`Show only stints whose wall span (max − min client_timestamp in the filtered window) is at least ${fmtMs(longSessions.minSpanMs)}. Leave both Long and Paid unchecked to browse every interaction stint.`}
                  label="Long filter"
                />
              </span>
            </label>
            <label className="inline-flex cursor-pointer items-center gap-1.5">
              <input
                type="checkbox"
                className="accent-accent-2"
                checked={paidOnly}
                onChange={(e) => onPaidOnlyChange(e.target.checked)}
              />
              <span className="inline-flex items-center gap-0.5">
                Paid 💵
                <InfoPopover
                  text="Show only stints whose interaction_session_id is on the Retention paid session list, including shell-gap payments (any wall span). A paid_session_id alone does not qualify."
                  label="Paid filter"
                />
              </span>
            </label>
          </div>
        }
      >
        {showUnexpectedMismatchBanner ? (
          <p className="mb-2 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[11px] leading-snug text-text-primary">
            <strong>Unexpected:</strong> Long and Paid are off but the explorer lists{" "}
            {longSessions.total.toLocaleString()} of {summary.stintCount.toLocaleString()} interaction stints.
            Check the API source badge (top right) — hosted Production/Staging APIs may still use the old
            long-or-paid default until deployed. Local dev should use <strong>Local API</strong> on port 6180.
          </p>
        ) : null}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse text-left text-[12px]">
            <thead>
              <tr className="border-b border-border-subtle text-[11px] uppercase tracking-wide text-text-secondary">
                <ExplorerColumnHeader
                  label="Session id"
                  info="interaction_session_id from the games shell. Table shows … plus the last 4 characters; hover a cell for the full id. Click a row to open Raw Events filtered to that session."
                />
                <ExplorerColumnHeader
                  label="Wall"
                  info="Wall clock span: max(client_timestamp) − min(client_timestamp) per id in the filtered window. Idle gaps between events count toward wall time."
                />
                <ExplorerColumnHeader
                  label="Active"
                  info="Sum of screen_exited.duration_ms for attract, main_menu, and game screens in this stint — time the shell reported on those screens, not wall clock."
                />
                <ExplorerColumnHeader
                  label="Events"
                  info="Number of device_events rows for this interaction_session_id within the current dashboard filters."
                />
                <ExplorerColumnHeader
                  label="Device"
                  info="Friendly name from devices.name when set; otherwise a shortened device id. Full device uuid on hover when a name is shown."
                />
                <ExplorerColumnHeader
                  label="Venue"
                  info="When the stint has properties.paid_session_id or play_session_id on any event, venue comes from play_sessions.venue_id (frozen at session creation — correct if the device moved). Otherwise falls back to devices.venue_id today (can mislabel old attract-only stints after a move)."
                />
                <ExplorerColumnHeader
                  label="Paid"
                  info="💵 when this stint’s interaction_session_id is on the Retention paid session list (registered or shell gap). A non-empty paid_session_id alone does not show 💵."
                />
                <ExplorerColumnHeader
                  label="Flags"
                  info="game = game_started; ✓ = game_completed; abandon = game_abandoned; pulse×N = coarse_uptime_pulse count in the stint."
                />
                <ExplorerColumnHeader
                  label="First / last (local)"
                  info="First and last client_timestamp for this stint, formatted in the reporting timezone."
                />
              </tr>
            </thead>
            <tbody>
              {longSessions.rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-2 py-4 text-text-secondary">
                    {emptyExplorerMessage}
                  </td>
                </tr>
              ) : (
                longSessions.rows.map((row) => {
                  const rawEventsUrl = buildRawEventsUrl({
                    filters,
                    eventName: "all",
                    interactionSessionId: row.interactionSessionId,
                  });
                  const venueName = venueDisplayName(row, venueLabelById);
                  return (
                    <tr
                      key={row.interactionSessionId}
                      className="cursor-pointer border-b border-border-subtle/60 hover:bg-[#1a2d4d]"
                      onClick={(e) => {
                        if (navigateOrOpenNewTab(rawEventsUrl, e) === "new") return;
                        onOpenRawEvents({
                          eventName: "all",
                          interactionSessionId: row.interactionSessionId,
                        });
                      }}
                      onAuxClick={(e) => {
                        if (e.button !== 1) return;
                        e.preventDefault();
                        navigateOrOpenNewTab(rawEventsUrl, e);
                      }}
                    >
                      <td
                        className="px-2 py-2 font-mono text-[11px]"
                        title={`${row.interactionSessionId} — click row: Raw Events`}
                      >
                        …{sessionIdTail(row.interactionSessionId)}
                      </td>
                      <td className="px-2 py-2 tabular-nums">{fmtMs(row.spanMs)}</td>
                      <td className="px-2 py-2 tabular-nums">{fmtMs(row.activeMs)}</td>
                      <td className="px-2 py-2 tabular-nums">{row.eventCount}</td>
                      <td className="px-2 py-2" title={row.deviceId}>
                        <div>{deviceDisplayName(row, deviceLabelById)}</div>
                        {deviceLabelById.has(row.deviceId) || row.deviceName.trim() ? (
                          <div className="font-mono text-[10px] text-text-secondary">
                            {truncateId(row.deviceId, 6)}
                          </div>
                        ) : null}
                      </td>
                      <td
                        className="px-2 py-2"
                        title={
                          row.venueSource === "play_session"
                            ? "Venue from play_sessions.venue_id (historical, frozen at session creation)"
                            : "Venue from devices.venue_id today — may differ if the device moved since this stint"
                        }
                      >
                        <div>{venueName || "—"}</div>
                        {row.venueId ? (
                          <div className="font-mono text-[10px] text-text-secondary" title={row.venueId}>
                            {truncateId(row.venueId, 8)}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 text-[11px] whitespace-nowrap">
                        {row.hadPaymentConfirmed || row.hadPaymentSucceeded ? (
                          <span title="On the Retention paid session list (including shell gap)">💵</span>
                        ) : (
                          <span className="text-text-secondary">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-[11px]">
                        {row.hadGameStart ? "game " : ""}
                        {row.hadGameCompleted ? "✓ " : ""}
                        {row.hadGameAbandoned ? "abandon " : ""}
                        {row.pulseCount > 0 ? `pulse×${row.pulseCount}` : ""}
                        {!row.hadGameStart &&
                        !row.hadGameCompleted &&
                        !row.hadGameAbandoned &&
                        row.pulseCount === 0 ? (
                          <span className="text-text-secondary">—</span>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 text-[10px] text-text-secondary whitespace-nowrap">
                        {formatEventTimeLocal(row.firstTs, reportingTz)}
                        <br />
                        {formatEventTimeLocal(row.lastTs, reportingTz)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-0 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-border-subtle pt-1.5 text-[11px] text-text-secondary">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
              onClick={onListPrevPage}
              disabled={listOffset <= 0}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
              onClick={onListNextPage}
              disabled={!listHasMore}
            >
              Next
            </button>
            <span className="tabular-nums">
              Page {listPage} of {listTotalPages}
            </span>
            <label className="inline-flex items-center gap-1">
              <span className="text-text-secondary">Rows/page</span>
              <select
                className="rounded border border-border-subtle bg-panel-2 px-1 py-0.5 text-[11px] text-text-primary"
                value={listLimit}
                onChange={(e) => onListPageSizeChange(Number.parseInt(e.target.value, 10))}
              >
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {longSessions.total > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex max-w-[min(100vw-24px,520px)] flex-wrap items-center gap-1 overflow-x-auto py-0.5">
                {pageNums.map((p, i) => (
                  <Fragment key={p}>
                    {i > 0 && pageNums[i - 1] !== undefined && p > pageNums[i - 1]! + 1 ? (
                      <span className="px-0.5 text-text-secondary" aria-hidden>
                        …
                      </span>
                    ) : null}
                    <button
                      type="button"
                      className={`min-w-[1.5rem] rounded border px-1.5 py-0.5 tabular-nums ${
                        p === listPage
                          ? "border-accent-2 bg-panel-3 font-semibold text-text-primary"
                          : "border-border-subtle bg-panel-2 text-text-primary hover:bg-[#223455]"
                      }`}
                      onClick={() => onGoToPage(p)}
                    >
                      {p}
                    </button>
                  </Fragment>
                ))}
              </div>
              <span className="inline-flex items-center gap-1 border-border-subtle border-l pl-2">
                <span>Go to</span>
                <input
                  type="number"
                  min={1}
                  max={listTotalPages}
                  className="w-11 rounded border border-border-subtle bg-panel-2 px-1 py-0.5 text-[11px] text-text-primary tabular-nums"
                  value={jumpDraft}
                  onChange={(e) => setJumpDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitJump();
                  }}
                  aria-label="Page number"
                />
                <button
                  type="button"
                  className="rounded border border-border-subtle bg-panel-2 px-1.5 py-0.5 text-[11px] text-text-primary hover:bg-[#223455]"
                  onClick={submitJump}
                >
                  Go
                </button>
              </span>
            </div>
          ) : null}
        </div>
      </ChartCard>

      <div className="mt-3 grid grid-cols-1 gap-[10px] xl:grid-cols-2">
        <ChartCard
          title="Stint duration (4 buckets)"
          criteria={`Distinct non-empty interaction_session_id. Per id: span_ms = max(client_timestamp) − min(client_timestamp) in filtered window. Buckets: <1m, 1–5m, 5–30m, 30m+.

Bar click: not wired (use cohort profile cards or session explorer).`}
          note="Wall clock between first and last stored row per id (gaps count). See session-model doc for shell rotation rules."
        >
          <BarChart
            data={durationBars}
            verbatimAxisLabels
            barTooltip={(item) =>
              `${item.label}: ${Math.round(item.value)} stints — cohort profiles below characterize event mix.`
            }
          />
        </ChartCard>

        <ChartCard
          title="Paid vs free by cohort"
          criteria={`Per interaction_session_id: paid = any row with non-empty paid_session_id; else free. Stacked counts by wall-span cohort (same span buckets as duration chart).

Not wired to Raw Events (composite session grain).`}
          note="Paid signal from properties.paid_session_id on events."
        >
          <BarChart
            data={paidFreeBars}
            verbatimAxisLabels
            barTooltip={(item, _i, seg) => {
              const pf = summary.paidVsFreeByCohort[item.label as InteractionSessionCohort];
              if (seg === 0) return `${item.label} paid: ${pf?.paid ?? 0} stints`;
              if (seg === 1) return `${item.label} free: ${pf?.free ?? 0} stints`;
              return `${item.label}: ${Math.round(item.value)} stints`;
            }}
          />
          <p className="mt-2 text-[11px] text-text-secondary">
            <span className="inline-block h-2 w-2 rounded-sm align-middle" style={{ background: PAID_FILL }} />{" "}
            paid_session_id ·{" "}
            <span className="inline-block h-2 w-2 rounded-sm align-middle" style={{ background: FREE_FILL }} />{" "}
            no paid id
          </p>
        </ChartCard>

        {orphanProfile.eventCount > 0 && (
          <ChartCard
            title="Orphan events (no interaction_session_id)"
            criteria={`device_events in scope where interaction_session_id is null or empty (e.g. before first attract touch).

Bar click: Raw Events → event_name (no cohort filter).`}
            note={`${orphanProfile.eventCount.toLocaleString()} rows without a stint id`}
          >
            <BarChart
              data={orphanProfile.topEvents.map((e) => ({
                label: e.eventName,
                value: e.pct,
              }))}
              percent
              verbatimAxisLabels
              axisLabelMaxChars={14}
              barTooltip={(item) =>
                `${item.label}: ${fmtPct(item.value)} of orphan events — click: Raw Events. Middle-click / Ctrl/Cmd+click: new tab.`
              }
              onBarClick={(item, _i, _si, e) => onOpenRawEvents({ eventName: item.label }, e)}
            />
          </ChartCard>
        )}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-[10px]">
        {COHORT_ORDER.map((c) => {
          const profile = cohortProfileByKey.get(c);
          if (!profile || profile.stintCount === 0) return null;
          return (
            <CohortProfileBlock key={c} profile={profile} onOpenRawEvents={onOpenRawEvents} />
          );
        })}
      </div>
    </>
  );
}
